import { createSignal, onMount, type ParentProps, Show } from "solid-js";
import { ErrorBoundary } from "solid-js/web";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { usePostHog } from "@/hooks/usePostHog";
import { devLog } from "@/libs/dev-log";
import { tryCatch } from "@/libs/try-catch";
import { useSubmitFeedback } from "./api";

type ErrorProps = {
  reset: () => void;
  err?: Error;
};

const AuthenticatedErrorView = (props: ErrorProps) => {
  const [feedback, setFeedback] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [submitted, setSubmitted] = createSignal(false);
  const postHog = usePostHog();
  const { mutateAsync: submitFeedback } = useSubmitFeedback();

  onMount(() => {
    devLog("props.err", props.err);
    if (props.err) {
      postHog.captureException(props.err);
    }
  });

  const handleSubmitFeedback = async (e: Event) => {
    e.preventDefault();
    setIsSubmitting(true);

    const [error] = await tryCatch(submitFeedback(feedback()));
    if (error) {
      console.error("Failed to submit feedback:", error);
      setIsSubmitting(false);
    } else {
      setSubmitted(true);
      setIsSubmitting(false);
      window.location.href = "/";
    }
  };

  return (
    <Show
      when={!submitted()}
      fallback={
        <div class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <Card class="w-full max-w-md">
            <div class="text-center p-6">
              <div class="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <p class="sr-only">Success Icon</p>
                <i class="ph-fill ph-check-circle text-green-600 text-2xl" />
              </div>
              <h2 class="text-green-600 text-xl font-semibold">Grazie</h2>
              <p class="text-gray-600 mt-2">
                Il tuo feedback è stato inviato. Ti stiamo reindirizzando alla
                home page...
              </p>
            </div>
          </Card>
        </div>
      }
    >
      <div class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card class="w-full max-w-md">
          <div class="text-center p-6">
            <div class="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <p class="sr-only">Error Icon</p>
              <i class="ph-fill ph-bug-droid text-red-600 text-2xl" />
            </div>
            <h2 class="text-xl font-semibold">
              Oops! Qualcosa è andato storto
            </h2>
            <p class="text-gray-600 mt-2">
              Ci dispiace, qualcosa è andato storto. Il tuo feedback ci aiuta a
              migliorare.
            </p>
          </div>
          <div class="p-6 pt-0">
            <form onSubmit={handleSubmitFeedback} class="space-y-4">
              <div class="space-y-2">
                <label for="feedback" class="text-sm font-medium">
                  Dimmi cosa è successo (opzionale)
                </label>
                <textarea
                  id="feedback"
                  class="w-full min-h-[100px] p-2 border rounded-md"
                  placeholder="Descrivi cosa stavi facendo quando è successo questo errore..."
                  value={feedback()}
                  onInput={(e) => setFeedback(e.currentTarget.value)}
                />
              </div>
              <div class="flex gap-2">
                <Button type="submit" disabled={isSubmitting()} class="flex-1">
                  {isSubmitting() ? "Invio..." : "Invia Feedback"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    window.location.reload();
                  }}
                  class="flex-1"
                >
                  Ricarica la pagina
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </Show>
  );
};

const UnauthenticatedErrorView = (props: ErrorProps) => {
  const postHog = usePostHog();

  onMount(() => {
    if (props.err) {
      postHog.captureException(props.err);
    }
  });
  return (
    <div class="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card class="w-full max-w-md">
        <div class="text-center p-6">
          <div class="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <p class="sr-only">Error Icon</p>
            <i class="ph-fill ph-bug-droid text-red-600 text-2xl" />
          </div>
          <h2 class="text-xl font-semibold">
            Spiacenti, qualcosa è andato storto
          </h2>
          <p class="text-gray-600 mt-2">
            Ci dispiace per l'inconveniente. Si è verificato un errore durante
            il processo della tua richiesta.
          </p>
        </div>
        <div class="p-6 pt-0 space-y-4">
          <Alert>
            <p class="sr-only">Alert Icon</p>
            <i class="ph-fill ph-alert-circle text-red-600 text-2xl" />
            <span>Error Code: {props.err?.message}</span>
          </Alert>
          <Button
            onClick={() => {
              window.location.href = "/";
            }}
            class="w-full"
          >
            Vai alla Home
          </Button>
        </div>
      </Card>
    </div>
  );
};

export function GlobalErrorManager(props: ParentProps) {
  const [auth] = useAuthV2();

  return (
    <ErrorBoundary
      fallback={(err, reset) => (
        <Show
          when={!auth.loading}
          fallback={
            <div class="min-h-screen flex items-center justify-center bg-gray-50">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          }
        >
          {auth.profile ? (
            <AuthenticatedErrorView reset={reset} err={err} />
          ) : (
            <UnauthenticatedErrorView reset={reset} err={err} />
          )}
        </Show>
      )}
    >
      {props.children}
    </ErrorBoundary>
  );
}
